/**
 * The ID List's URL state: which tab, which page, what search.
 *
 * 🚨 WHY THIS EXISTS. Tab and page used to be React state with no URL, and the Previous/Next
 * controls were `<button onClick>`. Crawlers do not click, so of 1,882 IDs only the 75 on page 1 were
 * ever in HTML a search engine could read — every metadata check passed while the content itself was
 * unreachable. Every tab and page now has an address, rendered on the server and linked with a real
 * `<a href>`.
 *
 * ⚠️ These URLs must NEVER carry `version`, `modpack` or `mods`. VersionContext re-applies those on
 * every query-string change (it keys on pathname + search), so a page link that kept a `?version=`
 * from a Saved-library open would silently undo the user's own version choice on every click.
 * `idListHref` builds from scratch for exactly that reason — never from the current search params.
 */

export const ID_LIST_PATH = '/tools/id-list';
export const ID_LIST_URL = `https://mcworldtools.com${ID_LIST_PATH}`;
export const ITEMS_PER_PAGE = 75;

export type DataType = 'all' | 'items' | 'blocks' | 'mobs' | 'enchantments' | 'effects';

export const DATA_TYPES: readonly DataType[] = ['all', 'items', 'blocks', 'mobs', 'enchantments', 'effects'];

export const TAB_LABELS: Record<DataType, string> = {
  all: 'All',
  items: 'Items',
  blocks: 'Blocks',
  mobs: 'Mobs/Entities',
  enchantments: 'Enchantments',
  effects: 'Effects',
};

/** The page's H1 and the base of its <title> — the words people actually search for. */
export const TAB_HEADINGS: Record<DataType, string> = {
  all: 'Minecraft ID List',
  items: 'Minecraft Item IDs',
  blocks: 'Minecraft Block IDs',
  mobs: 'Minecraft Mob & Entity IDs',
  enchantments: 'Minecraft Enchantment IDs',
  effects: 'Minecraft Effect IDs',
};

/**
 * What a row of each tab is called in prose ("1,196 block IDs"). Must describe what the tab actually
 * lists: the Items tab holds only items with NO block form (rowsForType), so "495 item IDs" would read
 * as the whole item registry — which is 1,537 on 26.2 — and undersell the page by 3x.
 */
export const TAB_NOUNS: Record<DataType, string> = {
  all: 'IDs',
  items: 'non-block item IDs (tools, weapons, food and more)',
  blocks: 'block IDs',
  mobs: 'mob and entity IDs',
  enchantments: 'enchantment IDs',
  effects: 'status effect IDs',
};

export interface IdListQuery {
  type: DataType;
  page: number;
  search: string;
}

type RawParams = URLSearchParams | Record<string, string | string[] | undefined>;

function read(raw: RawParams, key: string): string | null {
  if (raw instanceof URLSearchParams) return raw.get(key);
  const v = raw[key];
  return Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
}

function isDataType(value: string | null): value is DataType {
  return value !== null && (DATA_TYPES as readonly string[]).includes(value);
}

/**
 * Anything unrecognised falls back to the default rather than erroring: `?type=bogus` is the All tab,
 * `?page=abc` is page 1. The canonical URL is rebuilt from THESE values, so junk never becomes an
 * address of its own.
 */
export function parseIdListQuery(raw: RawParams): IdListQuery {
  const type = read(raw, 'type');
  const page = read(raw, 'page');
  return {
    type: isDataType(type) ? type : 'all',
    page: page !== null && /^\d{1,5}$/.test(page) ? Math.max(1, Number(page)) : 1,
    // Bounded: it is echoed into the input and, for the Saved library, arrives from a link.
    search: (read(raw, 'search') ?? '').slice(0, 100),
  };
}

/** The one way to build an ID List link. Omits defaults, so each state has exactly one spelling. */
export function idListHref({ type, page, search }: IdListQuery): string {
  const sp = new URLSearchParams();
  if (type !== 'all') sp.set('type', type);
  if (page > 1) sp.set('page', String(page));
  if (search) sp.set('search', search);
  const qs = sp.toString();
  return qs ? `${ID_LIST_PATH}?${qs}` : ID_LIST_PATH;
}

/**
 * Page numbers to link, with `null` for a gap: 1 … 4 5 [6] 7 8 … 26. The first and last pages are
 * always linked, so a crawler starting anywhere reaches both ends in one hop.
 */
export function pageWindow(current: number, total: number, radius = 2): (number | null)[] {
  if (total <= 1) return [];
  const out: (number | null)[] = [];
  const lo = Math.max(2, current - radius);
  const hi = Math.min(total - 1, current + radius);
  out.push(1);
  if (lo > 2) out.push(null);
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < total - 1) out.push(null);
  out.push(total);
  return out;
}
